import { SessionRecapHelper } from 'classes/helpers/session-recap.helper';
import useAnalyticsStore from 'components/sections/analytics/store/use-analytics-store';
import { ISessionRecapHit } from 'interfaces/session-recap/i-session-recap-hit';
import { PlateHelper, VISIBLE_STRIKEZONE } from 'lib_ts/classes/plate.helper';
import { StrikeZoneType } from 'lib_ts/enums/machine.enums';
import { IStrikeZone } from 'lib_ts/interfaces/i-strike-zone';
import { compact, first } from 'lodash';
import { useMemo } from 'react';
import useHittersStore from 'stores/use-hitters-store';

interface ISessionRecapHits {
  szDef: IStrikeZone;
  /** every hit in the session, derived */
  records: ISessionRecapHit[];
  /** records with pitch data, which is what the recap displays */
  filtered: ISessionRecapHit[];
  byID: Record<string, ISessionRecapHit>;
}

export const useSessionRecapHits = (): ISessionRecapHits => {
  const sessionMachine = useAnalyticsStore((s) => s.sessionMachine);
  const hits = useAnalyticsStore((s) => s.hits);

  const firstHitterID = useMemo(
    () => first(compact(hits.map((h) => h.hitter?._id))),
    [hits]
  );

  const hitters = useHittersStore((s) => s.hitters);

  const hitter = useMemo(
    () => hitters.find((h) => h._id === firstHitterID),
    [hitters, firstHitterID]
  );

  const szDef = useMemo(() => {
    switch (sessionMachine?.strikeZoneType) {
      case StrikeZoneType.MlbAbs: {
        return PlateHelper.getMlbAbsStrikeZone(hitter?.height_ft);
      }
      case StrikeZoneType.Stringer: {
        return PlateHelper.getStringerStrikeZone(hitter?.height_ft);
      }
      default: {
        return VISIBLE_STRIKEZONE;
      }
    }
  }, [hitter?.height_ft, sessionMachine?.strikeZoneType]);

  const records = useMemo(
    () => SessionRecapHelper.convertToRecap(hits, szDef),
    [hits, szDef]
  );

  const filtered = useMemo(
    () => records.filter((r) => !!r.hit.pitch?._id),
    [records]
  );

  const byID = useMemo(
    () =>
      records.reduce(
        (acc, r) => {
          acc[r.hit._id] = r;
          return acc;
        },
        {} as Record<string, ISessionRecapHit>
      ),
    [records]
  );

  return { szDef, records, filtered, byID };
};
